/**
 * PreviewPane
 *
 * Renders the builder preview using an iframe that loads the actual
 * production SDUI renderer. This ensures:
 * 1. Preview matches production behavior exactly
 * 2. CSS isolation between builder UI and preview
 * 3. Claude's CSS intuitions work correctly (100vh, etc.)
 *
 * Communication happens via postMessage.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Monitor,
  Tablet,
  Smartphone,
  Sun,
  Moon,
  Code,
  Eye,
  Undo2,
  Redo2,
  Settings,
  GitBranch,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from 'next-themes';
import type { LogEntry } from '../types/log-entry';
import type { ProcessGraph } from '../types/guided-builder';
import { LogContent } from './LogContent';
import { GraphPane } from './GraphPane';

type DeviceType = 'desktop' | 'tablet' | 'mobile';
type ThemeMode = 'light' | 'dark';
type ViewMode = 'preview' | 'graph' | 'code' | 'log';

const MAX_LOG_ENTRIES = 1000;

interface PreviewPaneProps {
  uiDefinition: unknown | null;
  className?: string;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  undoCount?: number;
  redoCount?: number;
  verbose?: boolean;
  onVerboseChange?: (verbose: boolean) => void;
  deepThinking?: boolean;
  onDeepThinkingChange?: (deepThinking: boolean) => void;
  /** When true, shows admin-only controls: verbose toggle, code tab, log tab */
  isAdmin?: boolean;
  /** Ref to sync log entries to for problem reports */
  logEntriesRef?: React.MutableRefObject<unknown[]>;
  /** Called when the iframe captures a screenshot (base64 data URL) */
  onScreenshotCaptured?: (dataUrl: string) => void;
  /** Draft arche ID for live preview with production ArcheProvider */
  archeId?: string;
  /** Process graph for visualization */
  processGraph?: ProcessGraph;
  /** Currently building node ID */
  currentNodeId?: string | null;
}

const deviceWidths: Record<DeviceType, string> = {
  desktop: 'w-full',
  tablet: 'w-[768px]',
  mobile: 'w-[375px]',
};

export function PreviewPane({
  uiDefinition,
  className,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  undoCount = 0,
  redoCount = 0,
  verbose = false,
  onVerboseChange,
  deepThinking = false,
  onDeepThinkingChange,
  isAdmin = false,
  logEntriesRef,
  onScreenshotCaptured,
  archeId,
  processGraph,
  currentNodeId,
}: PreviewPaneProps) {
  const { theme: appTheme } = useTheme();
  const [device, setDevice] = useState<DeviceType>('desktop');
  // Initialize from app theme, defaulting to dark if system
  const [theme, setTheme] = useState<ThemeMode>(appTheme === 'light' ? 'light' : 'dark');
  const [viewMode, setViewMode] = useState<ViewMode>('preview');
  const [isIframeReady, setIsIframeReady] = useState(false);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [unreadLogCount, setUnreadLogCount] = useState(0);
  const [blocklistEnabled, setBlocklistEnabled] = useState(true);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pendingUpdateRef = useRef<unknown | null>(null);
  const viewModeRef = useRef<ViewMode>(viewMode);
  viewModeRef.current = viewMode;

  const onScreenshotCapturedRef = useRef(onScreenshotCaptured);
  onScreenshotCapturedRef.current = onScreenshotCaptured;

  // Reset iframe ready state when archeId changes (iframe remounts via key)
  const prevArcheIdRef = useRef(archeId);
  useEffect(() => {
    if (prevArcheIdRef.current !== archeId) {
      setIsIframeReady(false);
      prevArcheIdRef.current = archeId;
    }
  }, [archeId]);

  // Sync log entries to ref for problem reports
  useEffect(() => {
    if (logEntriesRef) {
      logEntriesRef.current = logEntries;
    }
  }, [logEntries, logEntriesRef]);

  // Send message to iframe
  const sendToIframe = useCallback((message: unknown) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(message, '*');
    }
  }, []);

  // Handle messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from our iframe
      if (event.source !== iframeRef.current?.contentWindow) return;

      switch (event.data?.type) {
        case 'READY':
          setIsIframeReady(true);
          // Always send current state when iframe is ready
          sendToIframe({
            type: 'UPDATE_UI',
            payload: {
              uiDefinition: pendingUpdateRef.current ?? uiDefinition,
              theme,
            },
          });
          pendingUpdateRef.current = null;
          break;

        case 'LOG_ENTRY':
          setLogEntries((prev) => {
            const next = [...prev, event.data.payload as LogEntry];
            return next.length > MAX_LOG_ENTRIES ? next.slice(-MAX_LOG_ENTRIES) : next;
          });
          if (viewModeRef.current !== 'log') {
            setUnreadLogCount((c) => c + 1);
          }
          break;

        case 'SCREENSHOT_RESULT':
          if (typeof event.data.data === 'string') {
            onScreenshotCapturedRef.current?.(event.data.data);
          }
          break;

        case 'ACTION_EXECUTED':
          break;

        case 'ERROR':
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendToIframe, theme, uiDefinition]);

  // Send UI updates to iframe when uiDefinition changes
  useEffect(() => {
    if (isIframeReady) {
      sendToIframe({
        type: 'UPDATE_UI',
        payload: {
          uiDefinition,
          theme,
        },
      });

      // Capture a screenshot after the iframe has time to render
      if (uiDefinition && onScreenshotCapturedRef.current) {
        const timer = setTimeout(() => {
          sendToIframe({ type: 'CAPTURE_SCREENSHOT' });
        }, 500);
        return () => clearTimeout(timer);
      }
    } else {
      // Queue the update for when iframe is ready
      pendingUpdateRef.current = uiDefinition;
    }
  }, [uiDefinition, isIframeReady, sendToIframe, theme]);

  // Clear unread count when switching to log tab
  useEffect(() => {
    if (viewMode === 'log') {
      setUnreadLogCount(0);
    }
  }, [viewMode]);

  // Debounce-POST logs + UI definition to dev endpoint for external visibility
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const timer = setTimeout(() => {
      fetch('/v1/dev/builder-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logs: logEntries, uiDefinition }),
      }).catch(() => {
        // Dev convenience — not critical path
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [logEntries, uiDefinition]);

  const clearLogs = useCallback(() => {
    setLogEntries([]);
    setUnreadLogCount(0);
  }, []);

  // Send theme updates
  useEffect(() => {
    if (isIframeReady) {
      sendToIframe({
        type: 'UPDATE_THEME',
        payload: { theme },
      });
    }
  }, [theme, isIframeReady, sendToIframe]);

  // Send blocklist toggle to iframe
  useEffect(() => {
    if (isIframeReady) {
      sendToIframe({
        type: 'SET_BLOCKLIST',
        payload: { enabled: blocklistEnabled },
      });
    }
  }, [blocklistEnabled, isIframeReady, sendToIframe]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Preview controls */}
      <div className="flex items-center justify-between border-b px-4 py-2 shrink-0">
        {/* Left: Device selector and undo/redo */}
        <div className="flex items-center gap-2">
          {/* Undo/Redo buttons */}
          <div className="flex items-center gap-1 mr-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onUndo}
              disabled={!canUndo}
              title={
                undoCount > 0
                  ? `Undo (Ctrl+Z) \u2013 ${undoCount} step${undoCount !== 1 ? 's' : ''}`
                  : 'Undo (Ctrl+Z)'
              }
            >
              <Undo2 className="h-4 w-4" />
            </Button>
            {undoCount > 0 && (
              <span className="text-[10px] tabular-nums text-muted-foreground -ml-1 select-none">
                {undoCount}
              </span>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onRedo}
              disabled={!canRedo}
              title={
                redoCount > 0
                  ? `Redo (Ctrl+Shift+Z) \u2013 ${redoCount} step${redoCount !== 1 ? 's' : ''}`
                  : 'Redo (Ctrl+Shift+Z)'
              }
            >
              <Redo2 className="h-4 w-4" />
            </Button>
            {redoCount > 0 && (
              <span className="text-[10px] tabular-nums text-muted-foreground -ml-1 select-none">
                {redoCount}
              </span>
            )}
          </div>

          {/* Device selector — hidden below 1536px where there's no room to switch viewports */}
          <Tabs
            value={device}
            onValueChange={(v) => setDevice(v as DeviceType)}
            className="hidden 2xl:block"
          >
            <TabsList className="h-8">
              <TabsTrigger value="desktop" className="h-7 px-2">
                <Monitor className="h-4 w-4" />
              </TabsTrigger>
              <TabsTrigger value="tablet" className="h-7 px-2">
                <Tablet className="h-4 w-4" />
              </TabsTrigger>
              <TabsTrigger value="mobile" className="h-7 px-2">
                <Smartphone className="h-4 w-4" />
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Checkboxes, view mode, and theme toggles */}
        <div className="flex items-center gap-3">
          {/* Deep thinking & verbose toggles — inline on xl+, gear dropdown below */}
          <div className="hidden 2xl:flex items-center gap-3">
            <label
              className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none"
              title="Use Claude Opus 4.5 for complex problems"
            >
              <input
                type="checkbox"
                checked={deepThinking}
                onChange={(e) => onDeepThinkingChange?.(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-input"
              />
              Deep thinking
            </label>
            {isAdmin && (
              <label
                className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none"
                title="Show tool calls and system prompt"
              >
                <input
                  type="checkbox"
                  checked={verbose}
                  onChange={(e) => onVerboseChange?.(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-input"
                />
                Verbose
              </label>
            )}
          </div>

          {/* Compact gear menu for narrow screens */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 2xl:hidden">
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  onDeepThinkingChange?.(!deepThinking);
                }}
              >
                <input
                  type="checkbox"
                  checked={deepThinking}
                  readOnly
                  className="h-3.5 w-3.5 rounded border-input mr-2"
                />
                Deep thinking
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    onVerboseChange?.(!verbose);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={verbose}
                    readOnly
                    className="h-3.5 w-3.5 rounded border-input mr-2"
                  />
                  Verbose
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
            <TabsList className="h-8">
              <TabsTrigger value="preview" className="h-7 px-2">
                <Eye className="h-4 w-4 xl:mr-1" />
                <span className="hidden xl:inline">Preview</span>
              </TabsTrigger>
              <TabsTrigger value="graph" className="h-7 px-2">
                <GitBranch className="h-4 w-4 xl:mr-1" />
                <span className="hidden xl:inline">Graph</span>
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="code" className="h-7 px-2">
                  <Code className="h-4 w-4 xl:mr-1" />
                  <span className="hidden xl:inline">Code</span>
                </TabsTrigger>
              )}
              {isAdmin && (
                <TabsTrigger value="log" className="h-7 px-2 relative">
                  <span className="xl:mr-1 font-mono text-xs">&gt;</span>
                  <span className="hidden xl:inline">Log</span>
                  {unreadLogCount > 0 && viewMode !== 'log' && (
                    <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
                      {unreadLogCount > 99 ? '99+' : unreadLogCount}
                    </span>
                  )}
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
          >
            {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Preview/Code/Log area */}
      <div className="flex-1 overflow-auto bg-muted/30 p-4 min-h-0">
        <div
          className={cn(
            'mx-auto h-full rounded-lg border bg-background shadow-sm transition-all duration-300 overflow-hidden relative',
            deviceWidths[device],
            device !== 'desktop' && 'max-w-full',
          )}
        >
          {/* Key on archeId so iframe remounts once the draft arche is available */}
          <iframe
            key={archeId || 'no-arche'}
            ref={iframeRef}
            src={`/a/arche_builder/preview${archeId ? `?archeId=${archeId}` : ''}`}
            className={cn('w-full h-full border-0', viewMode !== 'preview' && 'hidden')}
            title="Arche Preview"
          />
          {viewMode === 'graph' && (
            <div className="w-full h-full">
              <GraphPane processGraph={processGraph} currentNodeId={currentNodeId} />
            </div>
          )}
          {isAdmin && viewMode === 'code' && <CodeContent uiDefinition={uiDefinition} />}
          {isAdmin && viewMode === 'log' && (
            <LogContent
              entries={logEntries}
              onClear={clearLogs}
              blocklistEnabled={blocklistEnabled}
              onBlocklistToggle={() => setBlocklistEnabled((v) => !v)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface CodeContentProps {
  uiDefinition: unknown | null;
}

function CodeContent({ uiDefinition }: CodeContentProps) {
  if (!uiDefinition) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p className="text-sm">No UI definition yet</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <pre className="p-4 text-xs">
        <code>{JSON.stringify(uiDefinition, null, 2)}</code>
      </pre>
    </div>
  );
}
